import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SIGNATURE_IMAGE_MAX_BYTES, type OwnerSignatureDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { blobToBase64 } from '../../lib/upload';
import { formatDateTime } from '../../lib/date';
import { Loading, StatusMessage } from '../ui/Feedback';

/**
 * 0.27.0 — the OWNER's signature (Account page): the typed name + title printed on documents they
 * sign, and an optional handwritten signature image (PNG / JPEG, ≤ 1 MB). Owner-only — the API
 * refuses staff. The image is previewed through the normal documents signed-url flow (only the
 * uploading owner can mint one). Nothing here is defaulted: the name and title are the owner's data.
 */

export const signatureKey = ['admin', 'org', 'signature'] as const;

function ImagePreview({ documentId }: { documentId: string }) {
  const url = useQuery({
    queryKey: ['admin', 'doc-url', documentId],
    queryFn: () => api.documents.signedUrl(documentId, { ttlSeconds: 3600 }),
    staleTime: 50 * 60 * 1000,
  });
  if (!url.data) return <p className="muted">Loading preview…</p>;
  return (
    <img
      src={url.data.url}
      alt="Your handwritten signature on file"
      style={{ maxWidth: '100%', maxHeight: '7rem', background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '0.5rem' }}
    />
  );
}

export function SignatureSection() {
  const sig = useQuery({ queryKey: signatureKey, queryFn: () => api.org.getSignature() });
  if (sig.isLoading) return <Loading label="Loading signature…" />;
  if (sig.isError || !sig.data) return <StatusMessage tone="err">{errorMessage(sig.error)}</StatusMessage>;
  return <SignatureEditor data={sig.data} />;
}

/** Mounted once the profile has loaded, so the fields start from the stored values. */
function SignatureEditor({ data }: { data: OwnerSignatureDTO }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(data.signerName ?? '');
  const [title, setTitle] = useState(data.signerTitle ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const adopt = (d: OwnerSignatureDTO) => {
    queryClient.setQueryData(signatureKey, d);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'move-out-delivery'] });
  };
  const save = useMutation({
    mutationFn: () => {
      if (!name.trim() || !title.trim()) throw new Error('Enter both your name and your title.');
      return api.org.updateSignature({ signerName: name.trim(), signerTitle: title.trim() });
    },
    onSuccess: adopt,
  });
  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose an image first.');
      const contentType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      return api.org.uploadSignatureImage({ filename: file.name || 'signature', contentType, contentBase64: await blobToBase64(file) });
    },
    onSuccess: (d) => {
      adopt(d);
      setFile(null);
    },
  });

  return (
    <section aria-labelledby="sig-heading" className="card stack" style={{ maxWidth: 480, marginTop: '1.5rem' }}>
      <h2 id="sig-heading" style={{ marginTop: 0 }}>
        Signature
      </h2>
      <p className="muted">
        Used when you sign a finalized security-deposit statement. Your typed name and title are printed
        on the signature page; a handwritten signature image is added when one is on file. Only you can
        see or change this.
      </p>

      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        {save.isSuccess && <StatusMessage tone="ok">Signature name and title saved.</StatusMessage>}
        <div>
          <label htmlFor="sig-name">Signer name</label>
          <input id="sig-name" value={name} maxLength={80} required autoComplete="name" onChange={(e) => setName(e.target.value)} />
          <p className="field-hint">Your full name, exactly as it should appear on the statement.</p>
        </div>
        <div>
          <label htmlFor="sig-title">Title</label>
          <input id="sig-title" value={title} maxLength={120} required autoComplete="organization-title" onChange={(e) => setTitle(e.target.value)} />
          <p className="field-hint">Your title and the landlord entity you sign for.</p>
        </div>
        <div>
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save name and title'}
          </button>
        </div>
      </form>

      <div className="stack">
        <h3 style={{ marginBottom: 0 }}>Handwritten signature image</h3>
        {data.image ? (
          <>
            <ImagePreview documentId={data.image.documentId} />
            <p className="muted">
              On file since {formatDateTime(data.image.uploadedAt)} ({Math.max(1, Math.round(data.image.sizeBytes / 1024))} KB).
            </p>
          </>
        ) : (
          <p className="muted">None on file — statements you sign will carry your typed signature only.</p>
        )}
        {upload.isError && <StatusMessage tone="err">{errorMessage(upload.error)}</StatusMessage>}
        {upload.isSuccess && <StatusMessage tone="ok">Signature image saved.</StatusMessage>}
        {fileError && <StatusMessage tone="err">{fileError}</StatusMessage>}
        <div>
          <label htmlFor="sig-image">{data.image ? 'Replace with' : 'Upload'} a PNG or JPEG (up to 1 MB)</label>
          <input
            id="sig-image"
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFileError(null);
              if (f && f.size > SIGNATURE_IMAGE_MAX_BYTES) {
                setFile(null);
                setFileError('That image is larger than 1 MB. Crop it to just the signature or save it smaller.');
                return;
              }
              if (f && !/^image\/(png|jpeg)$/.test(f.type)) {
                setFile(null);
                setFileError('Use a PNG or JPEG image.');
                return;
              }
              setFile(f);
            }}
          />
          <p className="field-hint">Sign on white paper, photograph or scan it, and crop closely around the signature.</p>
        </div>
        <div>
          <button type="button" disabled={!file || upload.isPending} onClick={() => upload.mutate()}>
            {upload.isPending ? 'Uploading…' : data.image ? 'Replace image' : 'Upload image'}
          </button>
        </div>
      </div>
    </section>
  );
}
